import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/auth/auth.service';
import { WEBSOCKET_EVENTS } from 'src/app/shared/constants/websocket-room.constants';
import { SocketService } from './socket.service';

type ContractToastPayload = {
  event?: string;
  audience?: 'buyer' | 'vendor' | 'both';
  contractId?: string;
  contractName?: string;
  version?: string;
  senderId?: string | number;
  senderName?: string;
  buyerId?: string | number;
  timestamp?: number;
  data?: Record<string, unknown>;
};

/**
 * App-wide toasts for contract lifecycle socket events (any route).
 * Mirrors vendor event/bid socket toast services.
 */
@Injectable({ providedIn: 'root' })
export class AdminContractSocketToastService {
  private readonly socketService = inject(SocketService);
  private readonly toastr = inject(ToastrService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly recentToastKeys = new Map<string, number>();

  constructor() {
    this.socketService.socketContractData
      .pipe(
        filter((p: any) => !!p?.contractId && !!p?.event),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => this.handlePayload(payload as ContractToastPayload));
  }

  private handlePayload(payload: ContractToastPayload): void {
    const audience = payload.audience ?? 'both';
    if (audience === 'vendor') {
      return;
    }

    const userData = this.authService.getUserData();
    const myUserId = userData?.id ?? userData?.userId ?? userData?.user_id;

    // Prefer updates for this buyer when buyerId is present
    if (
      payload.buyerId != null &&
      myUserId != null &&
      String(payload.buyerId) !== String(myUserId)
    ) {
      return;
    }

    // Skip echo of own actions
    if (
      payload.senderId != null &&
      myUserId != null &&
      String(payload.senderId) === String(myUserId)
    ) {
      return;
    }

    const toast = this.messageFor(payload);
    if (!toast) {
      return;
    }

    const dedupeKey = `${payload.event}|${payload.contractId}|${payload.version ?? ''}|${payload.timestamp ?? ''}`;
    if (this.isRecentDuplicate(dedupeKey)) {
      return;
    }

    this.toastr.info(toast.body, toast.title);
  }

  private messageFor(
    payload: ContractToastPayload,
  ): { title: string; body: string } | null {
    const name = String(payload.contractName ?? '').trim() || 'contract';
    const sender = String(payload.senderName ?? '').trim();
    const version = String(payload.version ?? '').trim();
    const versionSuffix = version ? ` (v${version.replace(/^v/i, '')})` : '';
    const bySender = sender ? ` by ${sender}` : '';
    const events = WEBSOCKET_EVENTS.CONTRACT;

    switch (payload.event) {
      case events.SENT:
        return {
          title: 'Contract Sent',
          body: `Contract "${name}" has been sent to the vendor.`,
        };
      case events.NEGOTIATED:
        return {
          title: 'Contract Negotiation Update',
          body: `Contract "${name}" has a negotiation update${bySender}${versionSuffix}.`,
        };
      case events.VERSION_UPDATED:
        return {
          title: 'Contract Version Updated',
          body: `Version updated for "${name}"${versionSuffix}.`,
        };
      case events.ACCEPTED:
        return {
          title: 'Contract Accepted',
          body: `Contract "${name}" has been accepted${bySender}${versionSuffix}.`,
        };
      case events.REJECTED: {
        const reason = this.asToastText(payload.data?.['rejectReason']);
        const reasonText =
          reason && reason !== 'Not provided' ? ` Reason: ${reason}` : '';
        return {
          title: 'Contract Rejected',
          body: `Contract "${name}" has been rejected${bySender}.${reasonText}`,
        };
      }
      case events.EXTENSION_REQUESTED: {
        const extensionType = this.asToastText(payload.data?.['extensionType']);
        const typeLabel = extensionType ? ` (${extensionType})` : '';
        return {
          title: 'Extension Requested',
          body: `An extension was requested for "${name}"${typeLabel}${bySender}.`,
        };
      }
      case events.ESIGN_PENDING:
        return {
          title: 'E-Sign Pending',
          body: `Contract "${name}" is pending e-signature.`,
        };
      case events.ESIGN_COMPLETED: {
        const party = this.asToastText(payload.data?.['partyType']) || sender;
        const partyLabel = party ? ` (${party})` : '';
        return {
          title: 'E-Sign Completed',
          body: `E-signature completed for "${name}"${partyLabel}.`,
        };
      }
      case events.EXECUTED:
        return {
          title: 'Contract Executed',
          body: `Contract "${name}" has been executed.`,
        };
      case events.LIVE:
        return {
          title: 'Contract Live',
          body: `Contract "${name}" is now live.`,
        };
      case events.EXPIRED:
        return {
          title: 'Contract Expired',
          body: `Contract "${name}" has expired.`,
        };
      default:
        return null;
    }
  }

  private asToastText(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return '';
  }

  private isRecentDuplicate(key: string): boolean {
    const now = Date.now();
    const ttlMs = 15000;
    const last = this.recentToastKeys.get(key);
    if (last != null && now - last < ttlMs) {
      return true;
    }
    this.recentToastKeys.set(key, now);
    return false;
  }
}
